import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, finalize, map, Observable, tap, throwError } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import {
  ICreateOrderPayload,
  IOrder,
  IPaginatedResponse,
  OrderStatus
} from '../../interfaces/product.interface';
import { apiErrorMessage } from './products.service';

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  readonly #http: HttpClient = inject(HttpClient);
  readonly #toastrService: ToastrService = inject(ToastrService);
  readonly #translate: TranslateService = inject(TranslateService);
  readonly #apiBaseUrl: string = `${environment.apiUrl}/products/orders`;

  #isSaving: WritableSignal<boolean> = signal(false);
  isSaving: Signal<boolean> = this.#isSaving.asReadonly();

  /** Clientes uniquement. Tous les articles doivent venir du même vendeur. */
  createOrder(payload: ICreateOrderPayload): Observable<IOrder> {
    return this.#run(
      this.#http.post<{ data: IOrder }>(this.#apiBaseUrl, payload).pipe(map(res => res.data)),
      'SHOP.CART.ORDER_SUCCESS'
    );
  }

  getMyOrders(page: number = 1, limit: number = 10, status?: OrderStatus | null): Observable<IPaginatedResponse<IOrder>> {
    return this.#http.get<IPaginatedResponse<IOrder>>(`${this.#apiBaseUrl}/my`, { params: this.#params(page, limit, status) });
  }

  getIncomingOrders(page: number = 1, limit: number = 10, status?: OrderStatus | null): Observable<IPaginatedResponse<IOrder>> {
    return this.#http.get<IPaginatedResponse<IOrder>>(`${this.#apiBaseUrl}/incoming`, { params: this.#params(page, limit, status) });
  }

  getOrder(orderId: number): Observable<IOrder> {
    return this.#http.get<{ data: IOrder }>(`${this.#apiBaseUrl}/${orderId}`).pipe(map(res => res.data));
  }

  /** Vendeur : pending → confirmed|cancelled → ready → delivered. */
  updateStatus(orderId: number, status: OrderStatus): Observable<unknown> {
    return this.#run(this.#http.patch(`${this.#apiBaseUrl}/${orderId}/status`, { status }), 'SHOP.ORDERS.STATUS_UPDATED');
  }

  /** Cliente : annulation possible tant que la commande est en attente. */
  cancelOrder(orderId: number, reason?: string): Observable<unknown> {
    return this.#run(this.#http.patch(`${this.#apiBaseUrl}/${orderId}/cancel`, { reason }), 'SHOP.ORDERS.CANCELLED');
  }

  #params(page: number, limit: number, status?: OrderStatus | null): HttpParams {
    let params = new HttpParams().set('page', page).set('limit', limit);
    if (status) params = params.set('status', status);
    return params;
  }

  #run<T>(request: Observable<T>, successKey: string): Observable<T> {
    this.#isSaving.set(true);
    return request.pipe(
      tap(() => this.#toastrService.success(this.#translate.instant(successKey))),
      catchError(error => {
        this.#toastrService.error(apiErrorMessage(error, this.#translate.instant('SHOP.ERRORS.GENERIC')));
        return throwError(() => error);
      }),
      finalize(() => this.#isSaving.set(false))
    );
  }
}
